import { useAssetUrl } from '../../common/Helpers';
import { Label } from '../label/Label';
import Styles from './Audio.module.css';
import { Telemetry } from '@shared/frontend';
import { RefObject, useMemo } from 'react';
import type { IAssetDTO } from '../../models/IAssetDTO';

/** Props for the Audio component. */
interface IAudioDTO {
  loadingText: string;
  audioFallback?: string;
  audioUrl?: string;
  assetDTO?: IAssetDTO;
  rtl?: boolean;
  classNameContainer?: string;
  classNameAudio?: string;
  classNameLoading?: string;
  ref?: RefObject<HTMLDivElement | null>;
  onError?: () => void;
  // Stable telemetry surface key. When set, the control emits a `media_played` event on every `play` (reliable across browsers, unlike click-on-native-controls).
  telemetryKey?: string;
}

/**
 * Renders an Audio control. Plays either a directly-supplied URL or an audio asset resolved from a manifest and bundle.
 *
 * @param props - Component props.
 * @returns The rendered Audio element.
 */
export function Audio(props: IAudioDTO) {
  const { loadingText, audioFallback, audioUrl, assetDTO, rtl, classNameContainer, classNameAudio, classNameLoading, ref, onError, telemetryKey } = props;
  const assetURL = useAssetUrl(assetDTO ?? null);
  // A directly-supplied URL takes precedence over a resolved asset (mirrors how Image prefers spriteUrl over assetDTO).
  const audioURL = audioUrl ?? assetURL;

  // Compute class names an inline styles
  const containerClassName = useMemo(() => (classNameContainer ? `${Styles.container} ${classNameContainer}` : Styles.container), [classNameContainer]);

  // Prefix textual content with the right-to-left marker when rtl so mixed-direction strings render correctly (mirrors Image's resolvedTitle).
  const resolvedFallback = useMemo(() => (rtl && audioFallback ? `\u200F${audioFallback}` : audioFallback), [rtl, audioFallback]);
  const resolvedLoadingText = useMemo(() => (rtl && loadingText ? `\u200F${loadingText}` : loadingText), [rtl, loadingText]);

  // Render Audio element
  return (
    <>
      {audioURL ? (
        <div ref={ref} className={containerClassName}>
          <audio
            className={classNameAudio}
            src={audioURL}
            controls
            preload="none"
            onError={onError}
            onPlay={telemetryKey ? () => Telemetry.MediaPlayed(Telemetry.MediaTypes.Audio, telemetryKey) : undefined}
          >
            {resolvedFallback && <Label text={resolvedFallback} />}
          </audio>
        </div>
      ) : (
        <div className={classNameLoading}>{resolvedLoadingText}</div>
      )}
    </>
  );
}
